<template>
    <v-container>
        <v-row>
            <v-col>
                <h1>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn icon
                                   @click="$router.push({name: 'party', params: {partyId: character.party.id}})"
                                   v-on="on"
                            >
                                <v-icon x-large>mdi-chevron-left</v-icon>
                            </v-btn>
                        </template>
                        <span>Go Back</span>
                    </v-tooltip>
                    Character - {{ character.name }}
                </h1>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" sm="6">
                <v-card>
                    <v-card-title>Character Information</v-card-title>

                    <v-card-text>
                        <v-row v-if="character.retired">
                            <v-col>
                                <div class="display-1 red--text">Retired</div>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" lg="6">
                                <div class="subtitle-2">General Information</div>
                                <div>Experience: {{ character.experience }} ({{ character.experience_at_next_level || "-"}})
                                    <alter-experience :character="character" @experienceAltered="viewCharacter"/>
                                </div>
                                <div>Gold: {{ character.gold }}
                                    <alter-gold :character="character" @goldAltered="viewCharacter"/>
                                </div>
                            </v-col>

                            <v-col cols="12" lg="6">
                                <div class="subtitle-2">Level Information</div>
                                <div>Level: {{ character.level }}</div>
                                <div>Ancestors: {{ character.ancestor_count }}</div>
                                <div>Possible Perks: {{ character.possible_perks_count }}</div>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col>
                                <v-textarea v-model="notes"
                                            outlined
                                            auto-grow
                                            @input="updateNotesDelay($route.params.characterId)"
                                            label="Notes"/>

                                <v-chip color="warning" v-if="notesSaving">
                                    <v-progress-circular indeterminate size="18" width="2" class="mr-4"/> Saving...
                                </v-chip>
                                <v-chip color="success" v-else>
                                    <v-icon class="mr-2">mdi-check</v-icon> Saved
                                </v-chip>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-divider/>

                    <v-card-actions>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon
                                       color="primary"
                                       :disabled="character.gold < 10"
                                       @click="donate(character)"
                                       v-on="on"
                                >
                                    <v-icon>mdi-currency-usd</v-icon>
                                </v-btn>
                            </template>
                            <span>Donate 10 gold to the Sanctuary of the Great Oak</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon
                                       color="error"
                                       @click="destroyCharacter(character)"
                                       v-on="on"
                                >
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                            </template>
                            <span>Delete Character</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon
                                       color="error"
                                       @click="retire(character)"
                                       v-on="on"
                                >
                                    <v-icon>mdi-coffin</v-icon>
                                </v-btn>
                            </template>
                            <span>Retire Character</span>
                        </v-tooltip>

                        <v-tooltip bottom v-if="character.level_below_prosperity">
                            <template v-slot:activator="{ on }">
                                <v-btn color="warning"
                                       @click="levelUpToProsperity(character)"
                                       v-on="on"
                                >
                                    <v-icon class="mr-2">mdi-arrow-up-bold-hexagon-outline</v-icon> Level Up
                                </v-btn>
                            </template>
                            <span>Retire Character</span>
                        </v-tooltip>

                        <v-tooltip bottom v-if="character.leveled_up">
                            <template v-slot:activator="{ on }">
                                <v-btn color="success"
                                       @click="verifyLeveledUp(character)"
                                       v-on="on"
                                >
                                    <v-icon class="mr-2">mdi-arrow-up-bold-hexagon-outline</v-icon> Leveled Up
                                </v-btn>
                            </template>
                            <span>Retire Character</span>
                        </v-tooltip>
                    </v-card-actions>
                </v-card>

                <v-card class="mt-6">
                    <v-card-title>Perks</v-card-title>

                    <v-card-text>
                        <v-row>
                            <v-col cols="12"
                                   v-for="(perk, perkIndex) in character.perks_list"
                                   :key="perkIndex">
                                <v-checkbox
                                        :disabled="character.chosen_perks_count >= character.possible_perks_count && checkbox.value === false"
                                        hide-details
                                        dense
                                        v-for="(checkbox, checkboxIndex) in perk.checkboxes"
                                        :key="checkboxIndex"
                                        @change="setPerks(character)"
                                        v-model="checkbox.value"
                                        :label="perk.description"/>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-divider/>

                    <v-card-actions>
                        Perk Checks: {{ character.perk_checks }}
                        <v-btn @click="lowerCharacterPerkChecks(character)" icon color="error">
                            <v-icon>mdi-minus-circle</v-icon>
                        </v-btn>
                        <v-btn @click="raiseCharacterPerkChecks(character)" icon color="primary">
                            <v-icon>mdi-plus-circle</v-icon>
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>

            <v-col cols="12" sm="6">
                <v-card>
                    <v-card-title>Items</v-card-title>

                    <v-card-text>
                        <div v-for="(item, itemIndex) in character.items" :key="itemIndex" class="mb-4">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon
                                           small
                                           color="primary"
                                           @click="sellItem(item)"
                                           v-on="on"
                                    >
                                        <v-icon>mdi-currency-usd-off</v-icon>
                                    </v-btn>
                                </template>
                                <span>Sell Item</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon
                                           small
                                           color="error"
                                           @click="removeItem(item)"
                                           v-on="on"
                                    >
                                        <v-icon>mdi-close-circle</v-icon>
                                    </v-btn>
                                </template>
                                <span>Remove Item</span>
                            </v-tooltip>

                            #{{ item.number }} - {{ item.slot }} - {{ item.title }}
                        </div>
                    </v-card-text>

                    <v-divider/>

                    <v-card-actions>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon
                                       color="primary"
                                       @click="$router.push({name: 'item-shop', params: {characterId: character.id}})"
                                       v-on="on"
                                >
                                    <v-icon>mdi-currency-usd</v-icon>
                                </v-btn>
                            </template>
                            <span>Buy Item</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn icon
                                       color="primary"
                                       @click="$router.push({name: 'grant-item', params: {characterId: character.id}})"
                                       v-on="on"
                                >
                                    <v-icon>mdi-shopping</v-icon>
                                </v-btn>
                            </template>
                            <span>Grant Item</span>
                        </v-tooltip>

                        <create-item-dialog :campaign="character.party.campaign" @itemStored="itemStored"/>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

    </v-container>
</template>

<script>
  import CharacterService from "../services/CharacterService";
  import AlterExperience from "./character/AlterExperience";
  import AlterGold from "./character/AlterGold";
  import CreateItemDialog from "./CreateItemDialog";

  export default {
    components: {
      AlterExperience,
      AlterGold,
      CreateItemDialog
    },

    data: () => ({
      character: null,
      notes: null,
      notesTimeout: null,
      notesSaving: false
    }),

    created() {
      this.viewCharacter();
    },

    methods: {
      viewCharacter() {
        CharacterService.view(this.$route.params.characterId)
          .then(response => {
            this.character = response.data.data;
            this.notes = this.character.notes;
          })
          .catch(err => {
            console.log(err);
          });
      },

      itemStored(item) {
        CharacterService.grantItem(this.character.id, item.id)
          .then(() => {
            this.viewCharacter();
          })
          .catch(err => {
            console.log(err);
          });
      },

      raiseCharacterPerkChecks(character) {
        CharacterService.raisePerkChecks(character.id)
          .then(() => {
            this.viewCharacter();
          })
          .catch(err => {
            console.log(err);
          });
      },

      lowerCharacterPerkChecks(character) {
        CharacterService.lowerPerkChecks(character.id)
          .then(() => {
            this.viewCharacter();
          })
          .catch(err => {
            console.log(err);
          });
      },

      donate(character) {
        if (confirm(`Are you sure you want to donate 10 gold to the Sanctuary of the Great Oak?`)) {
          CharacterService.donate(character.id)
            .then(() => {
              this.viewCharacter();
            })
            .catch(err => {
              console.log(err);
            });
        }
      },

      destroyCharacter(character) {
        if (confirm(`Are you sure you want to remove the character ${character.name}?`)) {
          CharacterService.destroy(character.id)
            .then(() => {
              this.$router.push({name: 'party', params: {partyId: this.character.party.id}});
            })
            .catch(err => {
              console.log(err);
            });
        }
      },

      retire(character) {
        if (confirm(`Are you sure you want to retire the character ${character.name}?`)) {
          CharacterService.retire(character.id)
            .then(() => {
              this.$router.push({name: 'party', params: {partyId: this.character.party.id}});
            })
            .catch(err => {
              console.log(err);
            });
        }
      },

      setPerks(character) {
        CharacterService.setPerks(character.id, {
          perks_list: character.perks_list
        })
          .then(() => {
            this.viewCharacter();
          })
          .catch(err => {
            console.log(err);
          });
      },

      levelUpToProsperity(character) {
        CharacterService.levelUpToProsperity(character.id)
          .then(() => {
            this.viewCharacter();
          })
          .catch(err => {
            console.log(err);
          });
      },

      verifyLeveledUp(character) {
        CharacterService.verifyLeveledUp(character.id)
          .then(() => {
            this.viewCharacter();
          })
          .catch(err => {
            console.log(err);
          });
      },

      sellItem(item) {
        if (confirm(`Are you sure you want to sell the item #${item.number} to the return price of ${Math.floor(item.cost / 2)} gold?`)) {
          CharacterService.sellItem(this.character.id, item.id)
            .then(() => {
              this.viewCharacter();
            })
            .catch(err => {
              console.log(err);
            });
        }
      },

      removeItem(item) {
        if (confirm(`Are you sure you want to remove the item #${item.number}?`)) {
          CharacterService.removeItem(this.character.id, item.id)
            .then(() => {
              this.viewCharacter();
            })
            .catch(err => {
              console.log(err);
            });
        }
      },

      updateNotesDelay(characterId) {
        this.notesSaving = true;

        if (this.notesTimeout) {
          clearTimeout(this.notesTimeout);
          this.notesTimeout = null;
        }

        this.notesTimeout = setTimeout(() => {
          this.updateNotes(characterId);
        }, 3000);
      },

      updateNotes(characterId) {
        CharacterService.updateNotes(characterId, {
          notes: this.notes
        })
          .then(() => {
            this.notesSaving = false;
          })
          .catch(err => {
            console.log(err);
            this.notesSaving = false;
          });
      }
    }
  };
</script>